import { formatReportDate } from "./estimateReport";
import { filterVisibleHeaderFields } from "./reportHeader";
import { formatCurrency } from "./estimateTotals";
import {
  computePayrollPeriodTotals,
  computeRowBaseGrossPay,
  aggregatePayrollRowsByEmployee,
} from "./payrollOvertime";
import { formatPayrollTypeLabel } from "./payrollHelpers";
import {
  formatSessionShiftLabel,
  isSameLocalCalendarDate,
} from "./dateTimeHelpers";

export const DEFAULT_PAYROLL_REPORT_OPTIONS = Object.freeze({
  showEmployeeInformation: true,
  showPayrollPeriod: true,
  showLabourDetails: true,
  detailLevel: "detailed",
  showDailyEntries: true,
  showHourlyRates: true,
  showBreaks: true,
  showProjectNames: true,
  showWorkCategories: true,
  showTrade: false,
  showNotes: false,
  showTotals: true,
});

export const PAYROLL_REPORT_OPTION_DEFS = [
  { key: "showEmployeeInformation", label: "Show Employee Information", future: false },
  { key: "showPayrollPeriod", label: "Show Payroll Period", future: false },
  { key: "showLabourDetails", label: "Show Labour Details", future: false },
  { key: "showDailyEntries", label: "Show Daily Entries", future: false, requiresDetailed: true },
  { key: "showHourlyRates", label: "Show Hourly Rates", future: false, requiresDetailed: true },
  { key: "showBreaks", label: "Show Breaks", future: false, requiresDetailed: true },
  { key: "showProjectNames", label: "Show Project Names", future: false, requiresDetailed: true },
  { key: "showWorkCategories", label: "Show Work Categories", future: false, requiresDetailed: true },
  { key: "showTrade", label: "Show Trade", future: false, requiresDetailed: true },
  { key: "showNotes", label: "Show Notes", future: true },
  { key: "showTotals", label: "Show Totals", future: false },
];

function mergePayrollReportOptions(options = {}) {
  return { ...DEFAULT_PAYROLL_REPORT_OPTIONS, ...options };
}

const AGGREGATE_EMPLOYEE_SCOPES = new Set([
  "my_time",
  "MY_TIME",
  "entire_team",
  "entire_organization",
  "MY_CREW",
  "ALL_EMPLOYEES",
]);

function isSpecificEmployeeScope(scope) {
  return Boolean(scope) && !AGGREGATE_EMPLOYEE_SCOPES.has(scope);
}

/**
 * Resolve contact fields for the employee the payroll report is about.
 */
export function resolveReportEmployeeContact({
  rows = [],
  employeeScope = null,
  employeeName = null,
  employees = [],
  currentUser = null,
} = {}) {
  let subjectUserId = null;

  if (isSpecificEmployeeScope(employeeScope)) {
    subjectUserId = employeeScope;
  } else if (
    employeeScope === "my_time" ||
    employeeScope === "MY_TIME" ||
    !employeeScope
  ) {
    subjectUserId = currentUser?.supabase_user_id || null;
  } else {
    const uniqueUserIds = [
      ...new Set(
        (rows || []).map((row) => row.supabase_user_id).filter(Boolean),
      ),
    ];

    if (uniqueUserIds.length === 1) {
      subjectUserId = uniqueUserIds[0];
    }
  }

  let subjectRecord = null;

  if (subjectUserId) {
    subjectRecord =
      employees.find(
        (employee) => employee.supabase_user_id === subjectUserId,
      ) ||
      (currentUser?.supabase_user_id === subjectUserId ? currentUser : null);
  }

  return {
    employee_name: subjectRecord?.employee_name || employeeName || "—",
    email: subjectRecord?.email || null,
    phone: subjectRecord?.phone || null,
  };
}

function formatWorkDate(dateIso) {
  if (!dateIso) return "—";

  try {
    return new Date(`${dateIso}T12:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateIso;
  }
}

function formatPayrollEntryDate(row) {
  const base = formatWorkDate(row.date_iso);

  if (!row.start_time || !row.end_time) {
    return base;
  }

  if (isSameLocalCalendarDate(row.start_time, row.end_time)) {
    return base;
  }

  return `${base} · ${formatSessionShiftLabel(row.start_time, row.end_time)}`;
}

export function formatPeriodLabel(startDate, endDate) {
  if (startDate && endDate) {
    return `${formatReportDate(`${startDate}T12:00:00`)} – ${formatReportDate(`${endDate}T12:00:00`)}`;
  }

  if (startDate) {
    return `From ${formatReportDate(`${startDate}T12:00:00`)}`;
  }

  if (endDate) {
    return `Through ${formatReportDate(`${endDate}T12:00:00`)}`;
  }

  return "—";
}

function normalizeRows(rows = []) {
  return (rows || []).map((row) => ({
    ...row,
    gross_pay: Number(row.gross_pay ?? computeRowBaseGrossPay(row)),
  }));
}

function sumBreakHours(rows) {
  return (rows || []).reduce(
    (sum, row) => sum + Number(row.break_seconds || 0) / 3600,
    0,
  );
}

function buildHeaderPresentation(meta, options) {
  const employeeName =
    meta?.employeeInfo?.employee_name ||
    meta?.employeeName ||
    "—";
  const email = meta?.employeeInfo?.email || null;
  const phone = meta?.employeeInfo?.phone || null;
  const periodLabel = formatPeriodLabel(meta?.startDate, meta?.endDate);

  const fields = filterVisibleHeaderFields([
    {
      key: "employee",
      label: "Employee",
      value: employeeName,
      show: options.showEmployeeInformation,
    },
    {
      key: "email",
      label: "Email",
      value: email,
      show: options.showEmployeeInformation && Boolean(email),
    },
    {
      key: "phone",
      label: "Phone",
      value: phone,
      show: options.showEmployeeInformation && Boolean(phone),
    },
    {
      key: "period",
      label: "Payroll Period",
      value: periodLabel,
      show: options.showPayrollPeriod,
    },
    {
      key: "generated",
      label: "Date",
      value: formatReportDate(new Date()),
      show: options.showPayrollPeriod,
    },
  ]);

  return {
    employeeName,
    email,
    phone,
    periodLabel,
    startDate: meta?.startDate || null,
    endDate: meta?.endDate || null,
    employeeScope: meta?.employeeScope || null,
    generatedDate: formatReportDate(new Date()),
    fields,
  };
}

function buildSummaryPresentation(rows, orgSettings) {
  const periodTotals = computePayrollPeriodTotals(rows, orgSettings);
  const breakHours = sumBreakHours(rows);

  return {
    entryCount: rows.length,
    regularHours: periodTotals.regularHours,
    overtimeHours: periodTotals.overtimeHours,
    totalHours: periodTotals.totalHours,
    breakHours,
    grossPay: periodTotals.grossPay,
  };
}

function buildDetailRow(row, options) {
  const paidHours = Number(row.total_hours || 0);
  const breakHours = Number(row.break_seconds || 0) / 3600;
  const rate = Number(row.hourly_rate || 0);
  const parts = [];

  if (options.showProjectNames && row.project_name) {
    parts.push(row.project_name);
  }

  if (options.showWorkCategories) {
    const categoryParts = [
      row.classification_name,
      row.payroll_type ? formatPayrollTypeLabel(row.payroll_type) : null,
    ].filter(Boolean);
    if (categoryParts.length > 0) {
      parts.push(categoryParts.join(" • "));
    }
  }

  let detail = null;
  const detailParts = [];

  detailParts.push(`${paidHours.toFixed(2)} hrs`);

  if (options.showBreaks && breakHours > 0) {
    detailParts.push(`${breakHours.toFixed(2)} hr break`);
  }

  if (options.showHourlyRates && rate > 0) {
    detailParts.push(`${formatCurrency(rate)}/hr`);
  }

  if (detailParts.length > 0) {
    detail = detailParts.join(" × ");
  }

  return {
    id: row.id,
    date: formatPayrollEntryDate(row),
    name: row.employee || "Employee",
    project: options.showProjectNames ? row.project_name || "—" : null,
    classification: options.showWorkCategories
      ? row.classification_name || "—"
      : null,
    payrollType: options.showWorkCategories
      ? row.payroll_type
        ? formatPayrollTypeLabel(row.payroll_type)
        : "—"
      : null,
    trade: options.showTrade ? row.trade_name || "—" : null,
    hours: paidHours,
    breakHours: options.showBreaks ? breakHours : null,
    rate: options.showHourlyRates ? rate : null,
    gross: Number(row.gross_pay || 0),
    status: row.payroll_status || null,
    detail,
    notes: null,
  };
}

function buildSummaryDetailRows(rows, orgSettings) {
  const aggregations = aggregatePayrollRowsByEmployee(rows, orgSettings);

  return Array.from(aggregations.values()).map((aggregation) => ({
    label: aggregation.rows[0]?.employee || "Employee",
    hours: aggregation.totalHours,
    regularHours: aggregation.regularHours,
    overtimeHours: aggregation.overtimeHours,
    amount: aggregation.grossPay,
    detail: `${aggregation.totalHours.toFixed(2)} hrs`,
  }));
}

function buildDetailPresentation(rows, options, orgSettings) {
  if (!options.showLabourDetails || rows.length === 0) {
    return {
      mode: "hidden",
      summaryRows: [],
      detailRows: [],
      summaryLabel: "Payroll Detail",
    };
  }

  if (options.detailLevel === "summary") {
    return {
      mode: "summary",
      summaryRows: buildSummaryDetailRows(rows, orgSettings),
      detailRows: [],
      summaryLabel: "Payroll Detail",
    };
  }

  if (!options.showDailyEntries) {
    return {
      mode: "summary",
      summaryRows: buildSummaryDetailRows(rows, orgSettings),
      detailRows: [],
      summaryLabel: "Payroll Detail",
    };
  }

  return {
    mode: "detailed",
    summaryRows: [],
    detailRows: rows.map((row) => buildDetailRow(row, options)),
    summaryLabel: "Payroll Detail",
  };
}

function countUniqueEmployees(rows = []) {
  const keys = new Set();

  rows.forEach((row) => {
    const key = row.supabase_user_id || row.employee;
    if (key) {
      keys.add(key);
    }
  });

  return keys.size;
}

export function shouldGroupPayrollReport(rows = [], meta = {}) {
  if (isSpecificEmployeeScope(meta.employeeScope)) {
    return false;
  }

  if (
    meta.employeeScope === "my_time" ||
    meta.employeeScope === "MY_TIME" ||
    meta.employeeScope === "crew_member"
  ) {
    return false;
  }

  return countUniqueEmployees(rows) > 1;
}

function groupPayrollRowsByEmployee(rows = []) {
  const groups = new Map();

  rows.forEach((row) => {
    const key = row.supabase_user_id || row.employee || "unknown";
    const existing = groups.get(key) || {
      userId: row.supabase_user_id || null,
      employeeName: row.employee || "Employee",
      rows: [],
    };

    if (row.employee) {
      existing.employeeName = row.employee;
    }

    existing.rows.push(row);
    groups.set(key, existing);
  });

  return Array.from(groups.values()).sort((left, right) =>
    left.employeeName.localeCompare(right.employeeName, undefined, {
      sensitivity: "base",
    }),
  );
}

function buildOverallSummary(sections = []) {
  const regularHours = sections.reduce(
    (sum, section) => sum + Number(section.summary?.regularHours || 0),
    0,
  );
  const overtimeHours = sections.reduce(
    (sum, section) => sum + Number(section.summary?.overtimeHours || 0),
    0,
  );
  const breakHours = sections.reduce(
    (sum, section) => sum + Number(section.summary?.breakHours || 0),
    0,
  );
  const grossPay = sections.reduce(
    (sum, section) => sum + Number(section.summary?.grossPay || 0),
    0,
  );

  return {
    employeeCount: sections.length,
    regularHours,
    overtimeHours,
    breakHours,
    grossPay,
  };
}

function buildGroupedReportHeader(meta, employeeCount, options) {
  const employeeName =
    meta.employeeName && /\d+\s+Employees/i.test(meta.employeeName)
      ? meta.employeeName
      : `${employeeCount} Employees`;

  return buildHeaderPresentation(
    {
      ...meta,
      employeeInfo: {
        employee_name: employeeName,
        email: null,
        phone: null,
      },
      employeeName,
    },
    {
      ...options,
      showEmployeeInformation: false,
      showPayrollPeriod: options.showPayrollPeriod,
    },
  );
}

/**
 * Build a structured payroll report model for one employee.
 */
export function buildSinglePayrollReportModel(rows, meta = {}, options = {}) {
  const mergedOptions = mergePayrollReportOptions(options);
  const orgSettings = meta.orgSettings || null;
  const normalizedRows = normalizeRows(rows);
  const employeeInfo = resolveReportEmployeeContact({
    rows: normalizedRows,
    employeeScope: meta.employeeScope,
    employeeName: meta.employeeName,
    employees: meta.employees || [],
    currentUser: meta.currentUser || null,
  });
  const headerMeta = {
    ...meta,
    employeeInfo,
    employeeName: employeeInfo.employee_name,
  };
  const summary = buildSummaryPresentation(normalizedRows, orgSettings);
  const detail = buildDetailPresentation(
    normalizedRows,
    mergedOptions,
    orgSettings,
  );

  return {
    generatedAt: new Date(),
    options: mergedOptions,
    header: buildHeaderPresentation(headerMeta, mergedOptions),
    summary,
    detail,
    totals: {
      regularHours: summary.regularHours,
      overtimeHours: summary.overtimeHours,
      grossPay: summary.grossPay,
      taxes: null,
      netPay: null,
    },
    notes: mergedOptions.showNotes ? meta?.notes?.trim() || null : null,
  };
}

function buildGroupedPayrollReportModel(rows, meta = {}, options = {}) {
  const mergedOptions = mergePayrollReportOptions(options);
  const normalizedRows = normalizeRows(rows);
  const employeeGroups = groupPayrollRowsByEmployee(normalizedRows);
  const sections = employeeGroups.map((group) =>
    buildSinglePayrollReportModel(group.rows, {
      ...meta,
      employeeScope: group.userId || meta.employeeScope,
      employeeName: group.employeeName,
    }, options),
  );
  const overallSummary = buildOverallSummary(sections);
  const notes = mergedOptions.showNotes ? meta?.notes?.trim() || null : null;

  return {
    isGrouped: true,
    generatedAt: new Date(),
    options: mergedOptions,
    header: buildGroupedReportHeader(meta, overallSummary.employeeCount, mergedOptions),
    sections,
    overallSummary,
    notes,
  };
}

/**
 * Resolve which queue rows feed payroll report generation.
 *
 * Selection is an inclusion override only:
 * - one or more selected ids → only those matching rows (queue order preserved)
 * - zero selected ids → full queue rows (existing behavior)
 *
 * Does not mutate rows or payroll_status.
 */
export function resolvePayrollReportRows(rows = [], selectedRowIds = []) {
  const queueRows = Array.isArray(rows) ? rows : [];
  const selectedIds = Array.isArray(selectedRowIds) ? selectedRowIds : [];

  if (selectedIds.length === 0) {
    return queueRows;
  }

  const selectedIdSet = new Set(selectedIds);
  return queueRows.filter((row) => selectedIdSet.has(row?.id));
}

/**
 * Build a structured payroll report model from time log rows + options.
 */
export function buildPayrollReportModel(rows, meta = {}, options = {}) {
  if (shouldGroupPayrollReport(rows, meta)) {
    return buildGroupedPayrollReportModel(rows, meta, options);
  }

  return buildSinglePayrollReportModel(rows, meta, options);
}

export function getPayrollTotalPresentation(reportModel) {
  if (!reportModel?.options?.showTotals) {
    return null;
  }

  const { totals } = reportModel;
  if (!totals) return null;

  const lines = [
    {
      key: "regularHours",
      label: "Regular Hours",
      value: `${Number(totals.regularHours || 0).toFixed(2)} hrs`,
      isCurrency: false,
    },
  ];

  if (Number(totals.overtimeHours || 0) > 0) {
    lines.push({
      key: "overtimeHours",
      label: "Overtime Hours",
      value: `${Number(totals.overtimeHours).toFixed(2)} hrs`,
      isCurrency: false,
    });
  }

  lines.push({
    key: "grossPay",
    label: "Gross Pay",
    value: formatCurrency(totals.grossPay || 0),
    isCurrency: true,
    emphasis: true,
  });

  if (totals.taxes != null) {
    lines.push({
      key: "taxes",
      label: "Taxes",
      value: formatCurrency(totals.taxes),
      isCurrency: true,
    });
  }

  if (totals.netPay != null) {
    lines.push({
      key: "netPay",
      label: "Net Pay",
      value: formatCurrency(totals.netPay),
      isCurrency: true,
      emphasis: true,
    });
  }

  return { lines };
}

export function getPayrollOverallSummaryPresentation(reportModel) {
  if (!reportModel?.isGrouped || !reportModel?.options?.showTotals) {
    return null;
  }

  const summary = reportModel.overallSummary;
  if (!summary) return null;

  return {
    title: "Overall Summary",
    lines: [
      {
        key: "employeeCount",
        label: "Employees",
        value: String(summary.employeeCount),
        isCurrency: false,
      },
      {
        key: "regularHours",
        label: "Total Regular Hours",
        value: `${Number(summary.regularHours || 0).toFixed(2)} hrs`,
        isCurrency: false,
      },
      ...(Number(summary.overtimeHours || 0) > 0
        ? [
            {
              key: "overtimeHours",
              label: "Total Overtime Hours",
              value: `${Number(summary.overtimeHours).toFixed(2)} hrs`,
              isCurrency: false,
            },
          ]
        : []),
      {
        key: "grossPay",
        label: "Total Gross Payroll",
        value: formatCurrency(summary.grossPay || 0),
        isCurrency: true,
        emphasis: true,
      },
    ],
  };
}

/**
 * Export payroll report as a PDF file (pdfmake + platform-aware delivery).
 */
export { downloadPayrollReportPdf as exportPayrollReportPdf } from "../pdf/payroll/downloadPayrollPdf";

/**
 * Legacy browser print export (deprecated).
 * Prints PayrollReportPrintDocument (portal mount), not the on-screen preview.
 * Disconnected from UI — retained until legacy print stack is removed.
 */
export function exportPayrollReportToPdf() {
  document.body.classList.add("estimate-report-printing", "payroll-report-printing");
  window.print();
  window.setTimeout(() => {
    document.body.classList.remove("estimate-report-printing", "payroll-report-printing");
  }, 1000);
}
